import React, { FC } from 'react';
import './ConfirmModal.css';
import Modal from '../Modal/Modal';
import { FaSpinner } from '@/Shared/Lib';
import { ConfirmData, ModalData } from '@/Shared';

interface ConfirmModalProps {
    /** Callback fired when user confirms the action */
    onConfirm: () => void;

    /** Callback fired when user cancels the action or closes the modal */
    onCancel: () => void;

    /** Data controlling the content, title, labels, and loading state of the modal */
    data?: ConfirmData | null;
}

/**
 * ConfirmModal component
 *
 * Reusable confirmation dialog for domain-level actions.
 * Visibility is determined entirely by the presence of `data`.
 * Designed to integrate with a generic Modal component.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 */
const ConfirmModal: FC<ConfirmModalProps> = ({ data, onConfirm, onCancel }) => {


	 /**
     * Modal content including buttons and custom body.
     * Stops click propagation to prevent accidental modal closure.
     * Displays a spinner if the action is in progress.
     */
	const confirmContent = (
		<div className={`ConfirmModal-backdrop`}>
			<div
				className="ConfirmModal-content"
				onClick={event => event.stopPropagation()}>
				{data?.content ?? ""}
			</div>
			<div className="ConfirmModal__footer flex-row">

				<button
				    type="button"
					className="ConfirmModal_cancel-button btn-outline"
					onClick={onCancel}>
					{data?.cancelLabel ?? "Annuler"}
				</button>

				<button
				    type="button"
					className="ConfirmModal__confirm-button btn-filled"
					onClick={() => !data?.isLoading && onConfirm()}>
					{data?.isLoading && <FaSpinner className='spinner' />}
					<span>{data?.confirmLabel ?? "Confirmer"}</span>
				</button>
			</div>
		</div>);


	/**
     * Wraps the confirm content in a generic ModalData object.
     * This allows us to use the standard Modal component for consistent styling.
     */
	const modalData: ModalData | undefined = data ? {
		title: data.title,
		content: confirmContent
	} : undefined;


	/**
     * Render the generic Modal.
     * The Modal is only displayed if modalData exists.
     * Closing the Modal triggers the onCancel callback.
     */
	return (<Modal data={modalData} onClose={onCancel} />);
};

export default ConfirmModal;